/**
 * F-076 — Texas Content Generation AI content studio (E-022 FE, Batch-8).
 * Taken over from antigravity's batch-8 assign (which stopped at branch
 * creation with no work committed).
 *
 * ACs: "TX topic prompt picker (SB 38 compliance guide, Houston Ch. 10 ASEC,
 * Dallas Ch. 27 registration walkthrough, TX landlord FAQ)", "Generate
 * SEO-optimized blog/marketing content", "Preview + edit", "Publish/distribute
 * via Brevo".
 *
 * The E-022 content-generation BE was retracted with the batch-8 dispatch and
 * never published, so generation binds to the nominated
 * CONTENT_STUDIO_PATHS.generate and degrades to an explicit "engine not live
 * state — this component never fabricates generated prose. The prompt builder
 * and SEO metadata are computed locally and work with the engine down.
 *
 * Market-gated to texas, registered at /texas/content-studio (group 'other').
 */
import React, { useMemo, useState } from 'react';
import { getAppMarket } from '../../services/MarketService';
import {
    GeneratedContent,
    TX_CONTENT_TOPICS,
    buildPrompt,
    buildSeoMeta,
    contentStudioApi,
} from '../../services/contentStudio';

export const TexasContentStudio: React.FC = () => {
    const isTexas = getAppMarket() === 'texas';
    const [topicId, setTopicId] = useState(TX_CONTENT_TOPICS[0].id);
    const [audience, setAudience] = useState('Texas rental property owners');
    const [words, setWords] = useState(900);
    const [generating, setGenerating] = useState(false);
    const [generated, setGenerated] = useState<GeneratedContent | null>(null);
    const [engineDown, setEngineDown] = useState(false);
    const [editedBody, setEditedBody] = useState('');
    const [publishing, setPublishing] = useState(false);
    const [publishNote, setPublishNote] = useState<string | null>(null);

    const topic = useMemo(
        () => TX_CONTENT_TOPICS.find((t) => t.id === topicId) || TX_CONTENT_TOPICS[0],
        [topicId],
    );
    const seo = useMemo(() => buildSeoMeta(topic), [topic]);
    const prompt = useMemo(() => buildPrompt(topic, { audience, words }), [topic, audience, words]);

    const generate = () => {
        setGenerating(true);
        setEngineDown(false);
        setPublishNote(null);
        contentStudioApi
            .generate({ topicId: topic.id, prompt, audience, words })
            .then((result) => {
                if (result) {
                    setGenerated(result);
                    setEditedBody(result.body);
                } else {
                    setGenerated(null);
                    setEditedBody('');
                    setEngineDown(true);
                }
            })
            .finally(() => setGenerating(false));
    };

    const publish = () => {
        if (!generated && !editedBody.trim()) return;
        setPublishing(true);
        setPublishNote(null);
        contentStudioApi
            .publish({ topicId: topic.id, seo, body: editedBody })
            .then((result) => {
                if (result) {
                    setPublishNote(`Published to Brevo${result.id ? ` (id ${result.id})` : ''}.`);
                } else {
                    setPublishNote('Publish route is not available yet — copy the content out below.');
                }
            })
            .finally(() => setPublishing(false));
    };

    if (!isTexas) return null;

    return (
        <div className="mx-auto max-w-6xl px-4 py-8" data-testid="texas-content-studio">
            <h1 className="text-2xl font-bold text-gray-900">Texas Content Studio</h1>
            <p className="mt-1 text-sm text-gray-600">
                Pick a Texas topic, build the prompt, generate SEO-optimised content, edit it, then publish to Brevo.
            </p>

            <div className="mt-4 grid gap-6 lg:grid-cols-3">
                <div>
                    <div className="text-xs font-semibold uppercase text-gray-500">Texas topic</div>
                    <div className="mt-2 space-y-2">
                        {TX_CONTENT_TOPICS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                data-testid="tx-topic-option"
                                onClick={() => setTopicId(t.id)}
                                className={`w-full rounded-lg border px-3 py-2 text-left ${
                                    topicId === t.id
                                        ? 'border-blue-400 bg-blue-50'
                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                                }`}
                            >
                                <div className="text-sm font-semibold text-gray-900" data-testid="tx-topic-title">
                                    {t.title}
                                </div>
                                <div className="text-xs text-gray-500">{t.reference}</div>
                            </button>
                        ))}
                    </div>

                    <div className="mt-4">
                        <label className="text-xs font-semibold uppercase text-gray-500" htmlFor="tx-audience">
                            Audience
                        </label>
                        <input
                            id="tx-audience"
                            className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
                            data-testid="tx-audience"
                            value={audience}
                            onChange={(e) => setAudience(e.target.value)}
                        />
                    </div>

                    <div className="mt-3">
                        <label className="text-xs font-semibold uppercase text-gray-500" htmlFor="tx-words">
                            Target length
                        </label>
                        <input
                            id="tx-words"
                            type="number"
                            min={300}
                            max={3000}
                            step={100}
                            className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
                            data-testid="tx-words"
                            value={words}
                            onChange={(e) => setWords(Number(e.target.value) || 900)}
                        />
                    </div>
                </div>

                <div>
                    <div className="text-xs font-semibold uppercase text-gray-500">Prompt</div>
                    <textarea
                        className="mt-2 h-56 w-full rounded border border-gray-300 p-2 text-xs"
                        data-testid="tx-prompt"
                        value={prompt}
                        readOnly
                    />

                    <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                        <div className="text-xs font-semibold uppercase text-gray-500">SEO metadata</div>
                        <div className="mt-1 text-sm">
                            <span className="text-gray-500">title: </span>
                            <span className="font-medium text-gray-900" data-testid="tx-seo-title">
                                {seo.title}
                            </span>
                        </div>
                        <div className="text-sm">
                            <span className="text-gray-500">slug: </span>
                            <span className="font-mono text-gray-900" data-testid="tx-seo-slug">
                                {seo.slug}
                            </span>
                        </div>
                        <div className="text-sm text-gray-700" data-testid="tx-seo-description">
                            {seo.description}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                            {seo.keywords.map((kw) => (
                                <span key={kw} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                                    {kw}
                                </span>
                            ))}
                        </div>
                    </div>

                    <button
                        type="button"
                        data-testid="tx-generate"
                        disabled={generating}
                        onClick={generate}
                        className="mt-3 w-full rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        {generating ? 'Generating…' : 'Generate content'}
                    </button>

                    {engineDown && (
                        <p className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="content-engine-down">
                            The Texas content engine is not live yet (E-022 backend). The prompt and SEO metadata above
                            are ready to send the moment it is — nothing is generated offline.
                        </p>
                    )}
                </div>

                <div>
                    <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold uppercase text-gray-500">Preview &amp; edit</div>
                        {generated && (
                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-700" data-testid="tx-generated-badge">
                                {generated.wordCount} words
                            </span>
                        )}
                    </div>

                    <input
                        className="mt-2 w-full rounded border border-gray-300 p-2 text-sm font-semibold"
                        data-testid="tx-content-title"
                        value={generated ? generated.title : ''}
                        readOnly={!generated}
                        placeholder="Generated title appears here"
                        onChange={(e) => generated && setGenerated({ ...generated, title: e.target.value })}
                    />

                    <textarea
                        className="mt-2 h-72 w-full rounded border border-gray-300 p-2 text-sm"
                        data-testid="tx-content-body"
                        value={editedBody}
                        onChange={(e) => setEditedBody(e.target.value)}
                        placeholder="Generated content appears here for review and editing."
                    />

                    <div className="mt-2 flex gap-2">
                        <button
                            type="button"
                            data-testid="tx-publish"
                            disabled={publishing || !editedBody.trim()}
                            onClick={publish}
                            className="rounded bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                            {publishing ? 'Publishing…' : 'Publish to Brevo'}
                        </button>
                        {!editedBody.trim() && (
                            <span className="self-center text-xs text-gray-500">Generate or paste content first</span>
                        )}
                    </div>

                    {publishNote && (
                        <p className="mt-2 text-xs text-gray-600" data-testid="tx-publish-note">
                            {publishNote}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TexasContentStudio;
